import { SuscriptionDataType } from "@/api/SuscriptionsApi";
import { FormConfigType } from "@/types/dynamicForm";
import { VALIDATION_ERROR_MESSAGE, Yup } from "@mavichpe/dynamic-forms";
import { EINVOICE_INVOICE_TYPE_CODE } from "./Constants";
import { validateIdentification } from "./Identification";
import { isValidPhoneNumber } from "./Phone";
import { resolveTaxpayer, TAXPAYER_NOT_REGISTERED_MESSAGE } from "./taxpayerRegistry";

export const getEmailYupValidation = (): any => {
    return Yup.string().required("El campo de email es obligatorio")
        .test("is-valid-emails", "Uno o más correos no son válidos", (value) => {
            if (!value) return false;

            // Separar por coma y limpiar espacios
            const emails = value.split(",").map((email) => email.trim());

            // Validar cada correo usando una expresión regular o Yup
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emails.every((email) => emailRegex.test(email));
        });
}

/**
 * Agrega a un campo de identificacion la validacion de formato de la nota 4 del
 * anexo v4.4 (incluye cedulas juridicas alfanumericas, bitacora del 22/04/2026).
 * El formato se verifica contra el tipo de identificacion del mismo formulario;
 * los valores vacios los sigue controlando el `required` de cada formulario.
 */
export const withIdentificationFormat = (
    schema: Yup.StringSchema,
    tipoFieldName: string = "tipo_identificacion"
): Yup.StringSchema =>
    schema.test("identificacion-formato", "Numero de identificacion invalido", function (value?: string) {
        if (!value) return true;
        const { valid, message } = validateIdentification(this.parent?.[tipoFieldName], value);
        return valid || this.createError({ message });
    });

/**
 * Agrega a un campo de identificacion la verificacion contra el padron de Tributacion.
 *
 * Solo falla ante una respuesta definitiva de "no inscrito": si Hacienda no contesta,
 * `resolveTaxpayer` devuelve `unknown` y el campo pasa. Bloquear por un fallo de red dejaria
 * al usuario sin poder guardar cada vez que `api.hacienda.go.cr` responde uno de sus 307.
 *
 * Es un test asincrono; Formik espera las validaciones async antes de ejecutar el submit.
 * Se encadena despues del de formato: `resolveTaxpayer` no sale a la red con un numero que
 * todavia no cumple la nota 4, y cachea por identificacion, asi que validar en cada tecla no
 * multiplica las consultas.
 */
export const withPadronRegistration = (
    schema: Yup.StringSchema,
    tipoFieldName: string = "tipo_identificacion"
): Yup.StringSchema =>
    schema.test("identificacion-padron", TAXPAYER_NOT_REGISTERED_MESSAGE, async function (value?: string) {
        if (!value) return true;
        const { status } = await resolveTaxpayer(this.parent?.[tipoFieldName], value);
        return status !== "not_registered";
    });

/**
 * Agrega a un campo de telefono la validacion del campo "NumTelefono" del anexo
 * v4.4: de 8 a 20 digitos, salvo numeros especiales como el 911.
 */
export const withPhoneFormat = (schema: Yup.StringSchema): Yup.StringSchema =>
    schema.test(
        "telefono-formato",
        "El telefono debe tener entre 8 y 20 digitos",
        (value?: string) => !value || isValidPhoneNumber(value)
    );

/** Condicion por defecto: el documento es una factura electronica. */
const isEinvoiceInvoice = (einvoiceKind: unknown): boolean =>
    einvoiceKind == EINVOICE_INVOICE_TYPE_CODE;

/**
 * Aplica `buildRequiredSchema` al campo solo cuando `isRequiredFor` se cumple para
 * el tipo de documento; en el resto de los casos el campo queda libre.
 *
 * IMPORTANTE: las validaciones de formato (`withPhoneFormat`,
 * `withIdentificationFormat`) deben construirse DENTRO de `buildRequiredSchema`.
 * Si se envuelve el `when` completo (`withPhoneFormat(Yup.string().when(...))`),
 * Yup deja el `test` en el schema base y lo ejecuta para todos los tipos de
 * documento, porque la condicion solo agrega `required`/`notRequired` sobre ese
 * mismo base.
 */
export const onlyWhenEinvoiceKind = (
    isRequiredFor: (einvoiceKind: unknown) => boolean,
    buildRequiredSchema: (schema: Yup.StringSchema) => Yup.StringSchema,
    einvoiceKindFieldName: string = "einvoice_kind"
): Yup.StringSchema =>
    Yup.string().when(einvoiceKindFieldName, {
        is: isRequiredFor,
        then: buildRequiredSchema,
        otherwise: (s) => s.notRequired(),
    });

/**
 * Telefono principal del receptor: obligatorio y con el formato del anexo v4.4
 * solo cuando el documento es una factura electronica. En los demas tipos el dato
 * puede venir de clientes historicos con telefonos incompletos y no debe bloquear
 * la emision.
 */
export const phoneRequiredForEinvoice = (
    einvoiceKindFieldName: string = "einvoice_kind"
): Yup.StringSchema =>
    onlyWhenEinvoiceKind(
        isEinvoiceInvoice,
        (s) => withPhoneFormat(s).required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
        einvoiceKindFieldName
    );

/**
 * Telefono secundario del receptor: nunca obligatorio (Hacienda solo pide un
 * "NumTelefono"), pero si viene con valor debe cumplir el formato del anexo v4.4
 * cuando el documento es una factura electronica.
 */
export const optionalPhoneForEinvoice = (
    einvoiceKindFieldName: string = "einvoice_kind"
): Yup.StringSchema =>
    onlyWhenEinvoiceKind(
        isEinvoiceInvoice,
        (s) => withPhoneFormat(s).notRequired(),
        einvoiceKindFieldName
    );

/**
 * Identificacion del receptor: obligatoria y con el formato de la nota 4 del anexo
 * v4.4 solo para los tipos de documento que la exigen. `isRequiredFor` se recibe
 * porque no todos coinciden: la factura de exportacion tambien la pide, mientras
 * que en el catalogo de clientes solo aplica a la factura electronica.
 */
export const identificationRequiredWhen = (
    isRequiredFor: (einvoiceKind: unknown) => boolean = isEinvoiceInvoice,
    tipoFieldName: string = "tipo_identificacion",
    einvoiceKindFieldName: string = "einvoice_kind"
): Yup.StringSchema =>
    onlyWhenEinvoiceKind(
        isRequiredFor,
        (s) => withIdentificationFormat(s, tipoFieldName)
            .required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
        einvoiceKindFieldName
    );

/**
 * Longitudes que el XSD v4.4 impone a `NombreComercial` (emisor y receptor): si se emite,
 * tiene que traer entre 3 y 80 caracteres. Vacio es valido porque el nodo es opcional.
 */
export const NOMBRE_COMERCIAL_MIN_LENGTH = 3;
export const NOMBRE_COMERCIAL_MAX_LENGTH = 80;

/**
 * Nombre comercial opcional: un `min(3)` pelado rechazaria el campo vacio, que es
 * legitimo, asi que la longitud minima solo se exige cuando el usuario escribio algo.
 */
export const nombreComercialValidation = (): Yup.StringSchema =>
    Yup.string()
        .max(NOMBRE_COMERCIAL_MAX_LENGTH, `El nombre comercial no puede tener mas de ${NOMBRE_COMERCIAL_MAX_LENGTH} caracteres`)
        .test(
            "nombre-comercial-min-length",
            `El nombre comercial debe tener al menos ${NOMBRE_COMERCIAL_MIN_LENGTH} caracteres`,
            (value) => {
                const trimmed = `${value || ""}`.trim();
                return !trimmed || trimmed.length >= NOMBRE_COMERCIAL_MIN_LENGTH;
            }
        )
        .notRequired();

export const loadReferenceFormLabel = (formConfig: FormConfigType, suscriptionData?: SuscriptionDataType) => {
    if (suscriptionData?.id) {
        const referenceInput = formConfig.sections.flatMap(s => s.fields).flat().find(f => f.name === "reference_number")
        if (suscriptionData.show_reference) {
            if (referenceInput) {
                referenceInput.visibilityHiddenIf = () => false // make the field visible
                referenceInput.label = `${suscriptionData.reference_label}`;
            }
        } else {
            if (referenceInput) {
                referenceInput.type = "hidden"
            }
        }
        return formConfig
    }
    return formConfig
}